// import JSZip from 'jszip';

// zip.js est chargé en global (script assets/js/zip.js), pas en import ES module.
// En mode module, `zip` nu provoque ReferenceError : toujours passer par window.zip.

import { sequence } from './promises';
import config from '../config';

const GENERIC_FILE_EXCLUDES = [/__MACOSX/];
const MEDIA_UPLOAD_BATCH = 6;
export const MEDIAS_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'bmp', 'gif', 'mp4', 'mp3', 'opus', 'aac', 'm4a', 'ogg', 'mov'];

function getZipApi() {
    const zipApi = typeof window !== 'undefined' ? window.zip : null;
    if(!zipApi) {
        throw new ReferenceError('zip is not defined');
    }
    return zipApi;
}

/** Charge zip.js une fois (même script que l'import conversation). */
export function ensureZipLoaded() {
    if(typeof window !== 'undefined' && window.zip) {
        return Promise.resolve(window.zip);
    }

    return new Promise((resolve, reject) => {
        if(typeof document === 'undefined') {
            reject(new ReferenceError('zip is not defined'));
            return;
        }

        const existing = document.querySelector('script[data-monlivresms-zipjs]');
        if(existing) {
            const onLoad = () => {
                existing.removeEventListener('load', onLoad);
                existing.removeEventListener('error', onError);
                if(window.zip) {
                    resolve(window.zip);
                } else {
                    reject(new ReferenceError('zip is not defined'));
                }
            };
            const onError = () => {
                existing.removeEventListener('load', onLoad);
                existing.removeEventListener('error', onError);
                reject(new Error('zip.js load failed'));
            };
            existing.addEventListener('load', onLoad);
            existing.addEventListener('error', onError);
            return;
        }

        const script = document.createElement('script');
        script.async = true;
        script.src = `${config.staticUrl}assets/js/zip.js`;
        script.dataset.monlivresmsZipjs = '1';
        script.onload = () => {
            if(window.zip) {
                resolve(window.zip);
            } else {
                reject(new ReferenceError('zip is not defined'));
            }
        };
        script.onerror = () => reject(new Error('zip.js load failed'));
        document.head.appendChild(script);
    });
}

export function readArchive(file, options = { includeRegexFiles: [], excludeRegexFiles: [] }, onProgress = percentile => {}) {
    console.log('readArchive launched...', options);

    return ensureZipLoaded().then(zipApi => {
        zipApi.workerScriptsPath = `${config.staticUrl}/assets/js/`;
        onProgress(1);
        return new Promise((resolve, reject) => {
            const reader = new zipApi.ZipReader(new zipApi.BlobReader(file));
            reader.getEntries().then(entries => {
                entries = entries
                    .filter(entry => (options.includeRegexFiles || []).some(includeRegex => includeRegex.test(entry.filename)))
                    .filter(entry => (options.excludeRegexFiles || []).concat(GENERIC_FILE_EXCLUDES).every(excludeRegex => !excludeRegex.test(entry.filename)));

                const percentile = 100 / entries.length;

                getFileContentRecursive(entries, results => {
                    resolve(results.map(entry => {
                        return {
                            ...entry,
                            filename: entry.path.split('/').pop(),
                        };
                    }));
                }, onProgress, reject);
            }).catch(reject);
        });
    });
}

function getFileContentRecursive(entries, onSuccess = () => {}, onProgress = percentile => {}, onError = err => {}, index = 0, results = []) {
    const entry = entries[index];
    if(entry) {
        getFileContent(entry).then(data => {
            onProgress(100 / entries.length);
            getFileContentRecursive(entries, onSuccess, onProgress, onError, index + 1, [...results, data]);
        }).catch(err => {
            onError(err);
        });
    } else {
        onSuccess(results);
    }
}

/**
 * Extrait d'un zip uniquement les médias dont le basename matche missingFilenames
 * (insensible à la casse). Remonte les fichiers avec le nom attendu (casing du rapport)
 * pour coller aux références CSV / S3 sans reparser la conversation.
 */
export function extractMissingMediasFromArchive(file, missingFilenames = [], onProgress = () => {}) {
    const wantedByLower = new Map();
    (missingFilenames || []).forEach(name => {
        if(name) {
            wantedByLower.set(String(name).toLowerCase(), name);
        }
    });
    if(!wantedByLower.size) {
        return Promise.resolve([]);
    }

    return ensureZipLoaded().then(zipApi => {
        zipApi.workerScriptsPath = `${config.staticUrl}/assets/js/`;
        onProgress(1);

        return new Promise((resolve, reject) => {
            const reader = new zipApi.ZipReader(new zipApi.BlobReader(file));
            reader.getEntries().then(entries => {
                const matched = [];
                const seen = new Set();
                (entries || []).forEach(entry => {
                    if(!entry || entry.directory) {
                        return;
                    }
                    const basename = String(entry.filename || '').split('/').pop();
                    const baseKey = basename.toLowerCase();
                    const expectedName = wantedByLower.get(baseKey)
                        || (!basename.includes('.') ? wantedByLower.get(`${baseKey}.jpg`) : null);
                    if(!expectedName || seen.has(expectedName.toLowerCase())) {
                        return;
                    }
                    if(GENERIC_FILE_EXCLUDES.some(regex => regex.test(entry.filename))) {
                        return;
                    }
                    seen.add(expectedName.toLowerCase());
                    matched.push({ entry, expectedName });
                });

                if(!matched.length) {
                    resolve([]);
                    return;
                }

                const percentile = 100 / matched.length;
                sequence(matched, ({ entry, expectedName }) => {
                    return getFileContent(entry).then(data => {
                        onProgress(percentile);
                        return {
                            ...data,
                            filename: expectedName,
                            path: entry.filename,
                        };
                    });
                }).then(resolve).catch(reject);
            }).catch(reject);
        });
    });
}

function classifyMedia(filename) {
    const path = `${filename || ''}`.split('?')[0].toLowerCase();
    const base = path.split('/').pop();
    const ext = base.includes('.') ? base.split('.').pop() : '';
    if(MEDIAS_EXTENSIONS.includes(ext)) {
        return { ext, type: mediaTypeForExtension(ext) };
    }
    // Instagram laisse parfois les JPEG du dossier photos sans extension.
    if(path.includes('/photos/') || path.includes('/stickers/')) {
        return { ext: 'jpg', type: 'image' };
    }
    return null;
}

function mediaTypeForExtension(ext) {
    switch(ext) {
        case 'mp4':
        case 'webm':
        case 'mov':
            return 'video';
        case 'mp3':
        case 'opus':
        case 'aac':
        case 'm4a':
        case 'ogg':
            return 'audio';
        default:
            return 'image';
    }
}

function getFileContent(entry) {
    return new Promise((resolve, reject) => {
        try {
            const zipApi = getZipApi();
            const kind = classifyMedia(entry.filename);
            if(kind) {
                const mime = `${kind.type}/${kind.ext.replace('jpeg', 'jpg')}`;
                entry.getData(new zipApi.BlobWriter(mime)).then(blob => {
                    resolve({ path: entry.filename, content: blob, type: kind.type });
                }).catch(reject);
            } else {
                entry.getData(new zipApi.TextWriter(null)).then(text => {
                    resolve({ path: entry.filename, content: text, type: 'text' });
                }).catch(reject);
            }
        } catch (err) {
            reject(err);
        }
    });
}

export function uniqueMediaBasenames(names = []) {
    const seen = new Set();
    const result = [];
    (names || []).forEach(name => {
        if(!name) {
            return;
        }
        const path = `${name}`.split('?')[0];
        let base = path.split('/').pop();
        if(base && !base.includes('.') && (/\/photos\//i.test(path) || /\/stickers\//i.test(path))) {
            base = `${base}.jpg`;
        }
        const key = base.toLowerCase();
        if(!key || seen.has(key)) {
            return;
        }
        seen.add(key);
        result.push(base);
    });
    return result;
}

function findZipEntry(byBase, name) {
    const key = `${name}`.toLowerCase();
    if(byBase.has(key)) {
        return byBase.get(key);
    }
    const stem = key.replace(/\.[^.]+$/, '');
    if(stem !== key && byBase.has(stem)) {
        return byBase.get(stem);
    }
    return null;
}

/**
 * Extrait puis confie les médias par petits lots.
 * Un lot est envoyé (onBatch) avant d'ouvrir le suivant, pour ne pas garder
 * des centaines de Mo de vocaux en mémoire.
 * shouldSkip(name) : fichier déjà transféré, non extrait.
 * onFileDone(name) : fichier sauté, absent ou illisible (pas ceux envoyés à onBatch).
 */
export function extractMediasInBatches(file, names = [], {
    batchSize = MEDIA_UPLOAD_BATCH,
    onBatch,
    onFileDone = () => {},
    shouldSkip = () => false,
} = {}) {
    const wanted = uniqueMediaBasenames(names);
    if(!wanted.length) {
        return Promise.resolve([]);
    }
    if(!onBatch) {
        return Promise.reject(new Error('NoMediaBatchHandler'));
    }

    return ensureZipLoaded().then(zipApi => {
        zipApi.workerScriptsPath = `${config.staticUrl}/assets/js/`;
        const reader = new zipApi.ZipReader(new zipApi.BlobReader(file));
        const close = () => reader.close().catch(() => {});

        return reader.getEntries().then(entries => {
            const byBase = new Map();
            (entries || []).forEach(entry => {
                if(!entry || entry.directory) {
                    return;
                }
                if(GENERIC_FILE_EXCLUDES.some(regex => regex.test(entry.filename || ''))) {
                    return;
                }
                const base = `${entry.filename || ''}`.split('/').pop().toLowerCase();
                if(base && !byBase.has(base)) {
                    byBase.set(base, entry);
                }
            });

            const pending = [];
            const skipped = [];
            wanted.forEach(name => {
                if(shouldSkip(name)) {
                    skipped.push(name.toLowerCase());
                    onFileDone(name);
                    return;
                }
                pending.push(name);
            });

            const run = index => {
                if(index >= pending.length) {
                    return Promise.resolve([]);
                }
                const slice = pending.slice(index, index + batchSize);
                return sequence(slice, name => {
                    const entry = findZipEntry(byBase, name);
                    if(!entry) {
                        onFileDone(name);
                        return Promise.resolve(null);
                    }
                    return getFileContent(entry).then(data => ({
                        ...data,
                        filename: name,
                    })).catch(err => {
                        console.error('Media extract skipped', name, err);
                        onFileDone(name);
                        return null;
                    });
                }).then(batch => {
                    const files = (batch || []).filter(Boolean);
                    const upload = files.length ? Promise.resolve(onBatch(files)) : Promise.resolve([]);
                    return upload.then(results => results || []);
                }).then(results => run(index + batchSize).then(rest => (results || []).concat(rest)));
            };

            return run(0).then(uploaded => close().then(() => skipped.concat(uploaded)));
        }).catch(err => close().then(() => Promise.reject(err)));
    });
}
